/**
 * Environment Configuration
 *
 * Handles environment variables and configuration for the ETP widget.
 * Uses Vite's import.meta.env for client-side environment variables.
 *
 * NOTE: In development mode with MSW, we override the BFF URL to use the mock server.
 * This check happens at runtime to ensure MSW mocks work correctly.
 */

interface DatadogConfig {
  /** Datadog application ID */
  applicationId?: string
  /** Datadog client token */
  clientToken?: string
  /** Datadog site (default: datadoghq.com) */
  site: string
  /** Service name for Datadog */
  service: string
  /** Environment name for Datadog */
  env: string
  /** Version for Datadog */
  version: string
  /** Session sample rate (0-100) */
  sampleRate: number
  /** Enable Datadog RUM */
  enabled: boolean
  /** Enable session replay */
  sessionReplay: boolean
  /** Track user interactions */
  trackInteractions: boolean
  /** Track resources */
  trackResources: boolean
  /** Track long tasks */
  trackLongTasks: boolean
  /** Default privacy level */
  defaultPrivacyLevel: 'allow' | 'mask' | 'mask-user-input'
  /** Enable heatmap */
  heatmap: boolean
  /** Enable feature flags */
  featureFlags: boolean
}

interface FingerprintConfig {
  /** Fingerprint.js public API key */
  apiKey: string
  /** Enable/disable fingerprinting */
  enabled: boolean
}
interface EnvConfig {
  /** CDN URL */
  cdnUrl: string
  /** BFF API base URL */
  bffApiUrl: string
  /** Google Maps API key */
  googleMapsApiKey: string
  /** Amplitude API key for analytics */
  amplitudeApiKey?: string
  /** Datadog RUM configuration */
  datadog: DatadogConfig
  /** Current environment */
  environment: 'development' | 'staging' | 'production'
  /** Is development mode */
  isDev: boolean
  /** Is production mode */
  isProd: boolean
  /** Fingerprint.js configuration */
  fingerprint: FingerprintConfig
}

/**
 * Detect current environment
 */
function detectEnvironment(): EnvConfig['environment'] {
  const mode = import.meta.env.MODE

  if (mode === 'production') {
    // Further distinguish between staging and production via hostname or env var
    const isStaging = typeof window !== 'undefined' && window.location.hostname.includes('staging')
    return isStaging ? 'staging' : 'production'
  }

  return 'development'
}

/**
 * Read an optional env var, treating both empty strings and unresolved `${VAR}`
 * placeholders as "not configured". A placeholder leaks through when an env file
 * ships the template value without substitution (e.g. `envsubst` never ran, or a
 * local `.env` escaped the `$`); the resulting literal is truthy and would
 * otherwise be handed to downstream SDKs, causing hard-to-diagnose 400s.
 */
function resolvedEnv(value?: string): string | undefined {
  if (!value || value.startsWith('${')) return undefined
  return value
}

/**
 * Environment configuration singleton
 */
export const env: EnvConfig = {
  cdnUrl: (import.meta.env.VITE_CDN_URL as string) || 'https://cdn.getbusie.com',
  bffApiUrl:
    (import.meta.env.VITE_BUSIE_ETP_BFF_SERVICE_API_URL as string) ||
    'http://localhost:3001/etp-bff',
  googleMapsApiKey: (import.meta.env.VITE_BUSIE_GOOGLE_MAPS_API_KEY as string) || '',
  amplitudeApiKey: resolvedEnv(import.meta.env.VITE_AMPLITUDE_API_KEY as string),
  fingerprint: {
    apiKey: (import.meta.env.VITE_FINGERPRINTJS_API_KEY as string) || '',
    enabled: import.meta.env.VITE_FINGERPRINTJS_ENABLED !== 'false',
  },
  datadog: {
    applicationId: (import.meta.env.VITE_RUM_APPLICATION_ID as string) || undefined,
    clientToken: (import.meta.env.VITE_RUM_CLIENT_TOKEN as string) || undefined,
    site: (import.meta.env.VITE_RUM_SITE as string) || 'datadoghq.com',
    service: (import.meta.env.VITE_RUM_SERVICE as string) || 'etp-widget',
    env: (import.meta.env.VITE_RUM_ENV as string) || detectEnvironment(),
    version: (import.meta.env.VITE_RUM_VERSION as string) || '1.0.0',
    sampleRate: parseInt((import.meta.env.VITE_RUM_SAMPLE_RATE as string) || '100', 10),
    enabled: import.meta.env.VITE_ENABLE_RUM === 'true',
    sessionReplay: import.meta.env.VITE_RUM_SESSION_REPLAY !== 'false',
    heatmap: import.meta.env.VITE_RUM_ENABLE_HEATMAP !== 'false',
    trackInteractions: import.meta.env.VITE_RUM_TRACK_INTERACTIONS !== 'false',
    trackResources: import.meta.env.VITE_RUM_TRACK_RESOURCES !== 'false',
    trackLongTasks: import.meta.env.VITE_RUM_TRACK_LONG_TASKS !== 'false',
    defaultPrivacyLevel:
      (import.meta.env.VITE_RUM_DEFAULT_PRIVACY_LEVEL as 'allow' | 'mask' | 'mask-user-input') ||
      'mask-user-input',
    featureFlags: import.meta.env.VITE_RUM_ENABLE_FEATURE_FLAGS !== 'false',
  },
  environment: detectEnvironment(),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
}

/**
 * Validate required environment variables
 * Call this at app initialization to fail fast on missing config
 */
export function validateEnv(): { valid: boolean; missing: string[] } {
  const envVars = {
    VITE_BUSIE_ETP_BFF_SERVICE_API_URL: import.meta.env
      .VITE_BUSIE_ETP_BFF_SERVICE_API_URL as string,
    VITE_BUSIE_GOOGLE_MAPS_API_KEY: import.meta.env.VITE_BUSIE_GOOGLE_MAPS_API_KEY as string,
  }

  const missing = Object.entries(envVars)
    .filter(([, value]) => !value)
    .map(([key]) => key)

  if (missing.length > 0 && env.isProd) {
    console.error('[ETP Widget] Missing required environment variables:', missing)
  }

  return {
    valid: missing.length === 0,
    missing,
  }
}

export default env
